import React, { useState, useEffect, useCallback, useRef } from 'react';
import './invoice-page.scss'; // Import CSS file for styling
import axios from 'axios';
import { isMonthlyProject, defaultHoursForProject, isInternalOnlyProject } from './invoiceHours';
require('dotenv').config();

function toLocalYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// First business day of the given calendar month (month 0-11).
function getFirstBusinessDayOfMonthFor(year, monthIndex) {
  const firstDayOfMonth = new Date(year, monthIndex, 1);
  const startPeriodDate = new Date(firstDayOfMonth);
  while (startPeriodDate.getDay() === 0 || startPeriodDate.getDay() === 6) {
    startPeriodDate.setDate(startPeriodDate.getDate() + 1);
  }
  return toLocalYMD(startPeriodDate);
}

// Last business day of the given calendar month (month 0-11).
function getLastBusinessDayOfMonthFor(year, monthIndex) {
  const lastDayOfMonth = new Date(year, monthIndex + 1, 0);
  const endPeriodDate = new Date(lastDayOfMonth);
  while (endPeriodDate.getDay() === 0 || endPeriodDate.getDay() === 6) {
    endPeriodDate.setDate(endPeriodDate.getDate() - 1);
  }
  return toLocalYMD(endPeriodDate);
}

function getFirstBusinessDayOfMonth() {
  const today = new Date();
  return getFirstBusinessDayOfMonthFor(today.getFullYear(), today.getMonth());
}

function getLastBusinessDayOfMonth() {
  const today = new Date();
  return getLastBusinessDayOfMonthFor(today.getFullYear(), today.getMonth());
}

function formatDisplayDate(ymd) {
  if (!ymd) {
    return '—';
  }
  const d = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) {
    return ymd;
  }
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const BILLING_MONTH_OPTIONS = [
  { value: '01', label: 'Jan' },
  { value: '02', label: 'Feb' },
  { value: '03', label: 'Mar' },
  { value: '04', label: 'Apr' },
  { value: '05', label: 'May' },
  { value: '06', label: 'Jun' },
  { value: '07', label: 'Jul' },
  { value: '08', label: 'Aug' },
  { value: '09', label: 'Sep' },
  { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' },
  { value: '12', label: 'Dec' },
];

function billingYearChoices(maxYear) {
  const end = Math.max(2025, maxYear);
  const years = [];
  for (let y = 2025; y <= end; y += 1) {
    years.push(y);
  }
  return years;
}

const InvoicePage = () => {
  const today = new Date();
  const [invoiceDate, setInvoiceDate] = useState(getLastBusinessDayOfMonth());
  const [selectedClient, setSelectedClient] = useState('All');
  const [startPeriod, setStartPeriod] = useState(getFirstBusinessDayOfMonth());
  const [endPeriod, setEndPeriod] = useState(getLastBusinessDayOfMonth());
  const [billingYear, setBillingYear] = useState(String(Math.max(2025, today.getFullYear())));
  const [billingMonth, setBillingMonth] = useState(String(today.getMonth() + 1).padStart(2, '0'));
  const [clientOptions, setClientOptions] = useState([]);
  const [invoiceTableData, setInvoiceTableData] = useState([]);
  const [saveClicked, setSaveClicked] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true); // State to control Submit button disablement
  const [recTotal, setRecTotal] = useState(0);
  const [editedHoursTxnIds, setEditedHoursTxnIds] = useState(new Set());
  const [invoiceStatus, setInvoiceStatus] = useState('INITIALIZED');
  const [isSaving, setIsSaving] = useState(false); // Loading state for Save button
  const [isSubmitting, setIsSubmitting] = useState(false); // Loading state for Submit button

  // Timeout handling states
  const [isLoading, setIsLoading] = useState(false);
  const [timeoutError, setTimeoutError] = useState(null);
  const fetchRequestIdRef = useRef(0);
  const periodRef = useRef({ start: startPeriod, end: endPeriod, inv: invoiceDate });
  const skipInitialClientReloadRef = useRef(true);
  const billingPeriodDroveClientRef = useRef(false);

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

    // const formatNumber = (num) => {
    // // Check if num is a valid number
    // if (isNaN(num)) {
    //     throw new Error('Invalid input: not a number');
    // }
    
    // // Convert num to a number if it's not already
    // num = Number(num);

    // // Format the number with commas as thousand separators
    // return num.toLocaleString();
    // };

  const fetchInvoiceData = useCallback(async (periodStart, periodEnd, invDate) => {
    const requestId = ++fetchRequestIdRef.current;
    setIsLoading(true);
    setTimeoutError(null);

    try {
      const token = sessionStorage.getItem('token');
      const userStr = sessionStorage.getItem('user');

      if (!userStr) {
        console.error('No user found in session storage');
        return;
      }

      const user = JSON.parse(userStr);
      const recruiterUserId = user?.id ?? user?.client_id;

      if (recruiterUserId == null || recruiterUserId === '') {
        console.error('No user id found for invoice fetch:', user);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_RYZ_SERVER}/get_client_transactions/${recruiterUserId}?period_start=${encodeURIComponent(periodStart)}&period_end=${encodeURIComponent(periodEnd)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      const data = response.data.filter(item => !isInternalOnlyProject(item.project_name));
      const formattedData = data.map(item => ({
        inv_date: invDate,
        candidate_id: item.candidate_id,
        period_start: periodStart,
        period_end: periodEnd,
        txn_id: parseInt(item.txn_id, 10),
        hours_worked: defaultHoursForProject(item.project_name, item.hours_worked),
        inv_value: 0,
        inv_status: 'NEW',
        client: item.client_name,
        candidate: item.candidate_name,
        project_name: item.project_name === 'NULL' ? '' : item.project_name,
        recruiter_price: item.recruiter_price,
        client_price: item.client_price,
        recruiter_total: item.recruiter_price,
        client_total: item.client_price,
        client_contact: item.client_contact,
        client_email: item.client_email,
        client_addr: item.client_addr,
        client_phone: item.client_phone,
        client_id: item.client_id,
      }));
      setInvoiceTableData(formattedData);
      const uniqueClients = [...new Set(formattedData.map(inv => inv.client).filter(Boolean))].sort();
      setClientOptions(uniqueClients);

      let rTotal = 0;
      for (const invoice of formattedData) {
        rTotal += invoice.recruiter_price * invoice.hours_worked;
      }
      setRecTotal(formatNumber(rTotal));
    } catch (error) {
      console.error('Error fetching data:', error);
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      const status = error.response?.status;
      if (status === 404) {
        setInvoiceTableData([]);
        setClientOptions([]);
        setRecTotal(formatNumber(0));
        setTimeoutError(null);
        return;
      }
      setTimeoutError({
        type: 'session_expired',
        message: 'Your session has expired. Please log in again.',
      });
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    periodRef.current = { start: startPeriod, end: endPeriod, inv: invoiceDate };
  }, [startPeriod, endPeriod, invoiceDate]);

  useEffect(() => {
    const maxYear = new Date().getFullYear();
    let year = parseInt(String(billingYear).trim(), 10);
    const month = parseInt(String(billingMonth).trim(), 10);
    if (Number.isNaN(year) || year < 2025) {
      return;
    }
    if (year > maxYear) {
      year = maxYear;
    }
    if (Number.isNaN(month) || month < 1 || month > 12) {
      return;
    }
    const monthIndex = month - 1;
    const nextStart = getFirstBusinessDayOfMonthFor(year, monthIndex);
    const nextEnd = getLastBusinessDayOfMonthFor(year, monthIndex);
    setBillingMonth(String(month).padStart(2, '0'));
    setBillingYear(String(year));
    setStartPeriod(nextStart);
    setEndPeriod(nextEnd);
    setInvoiceDate(nextEnd);
    billingPeriodDroveClientRef.current = true;
    setSelectedClient('All');
    setSaveClicked(false);
    setEditedHoursTxnIds(new Set());
    fetchInvoiceData(nextStart, nextEnd, nextEnd);
  }, [billingYear, billingMonth, fetchInvoiceData]);

  useEffect(() => {
    if (skipInitialClientReloadRef.current) {
      skipInitialClientReloadRef.current = false;
      return;
    }
    if (billingPeriodDroveClientRef.current) {
      billingPeriodDroveClientRef.current = false;
      return;
    }
    setEditedHoursTxnIds(new Set());
    const p = periodRef.current;
    fetchInvoiceData(p.start, p.end, p.inv);
  }, [selectedClient, fetchInvoiceData]);

  const yearSelectChoices = billingYearChoices(new Date().getFullYear());

  // Effect to manage Submit button state based on save button click
  useEffect(() => {
    // Enable "Create Final Invoice" button only when Save button has been clicked
    setSubmitDisabled(!saveClicked);
  }, [saveClicked]);

  const handleClientChange = (e) => {
    setSelectedClient(e.target.value);
  };

  const parseHoursInteger = (raw) => {
    const digits = String(raw).replace(/\D/g, '').slice(0, 4);
    if (digits === '') {
      return 0;
    }
    const n = parseInt(digits, 10);
    return Number.isFinite(n) ? n : 0;
  };

  // Function to handle changes in the hours in the invoice table
  const handleHoursChange = (invoice, e) => {
    if (isMonthlyProject(invoice.project_name)) {
      return;
    }
    const index = invoiceTableData.findIndex(inv => inv.txn_id === invoice.txn_id);
    if (index === -1) return;
    const newData = [...invoiceTableData];
    newData[index].hours_worked = parseHoursInteger(e.target.value);
    setEditedHoursTxnIds(prev => new Set([...prev, invoice.txn_id]));
    let rTotal = 0;
    for (const inv of newData) {
      rTotal += inv.recruiter_price * inv.hours_worked;
    }
    setRecTotal(formatNumber(rTotal));
    setInvoiceTableData(newData);
  };

  const submitInvoice = async () => {
    setIsSubmitting(true); // Show spinner
    try {
      // Step 1: Group the invoice table data by client and project_name combination
      const clientProjectGroups = {};
      invoiceTableData.forEach(invoice => {
        if (isInternalOnlyProject(invoice.project_name)) {
          return;
        }
        const groupKey = `${invoice.client}_${invoice.project_name || 'No Project'}`;
        if (!clientProjectGroups[groupKey]) {
          clientProjectGroups[groupKey] = [];
        }
        clientProjectGroups[groupKey].push(invoice);
      });

      // Step 2: Iterate through each client-project group and submit the invoice
      for (const groupKey in clientProjectGroups) {
        const clientProjectInvoices = clientProjectGroups[groupKey];
        //console.log("client-project invoices ", clientProjectInvoices);

        const due_date = new Date(new Date(clientProjectInvoices[0].period_end).setMonth(new Date(clientProjectInvoices[0].period_end).getMonth() + 1)).toISOString().split('T')[0];
        let totalClientPrice = 0;
        let totalRecruiterPrice = 0;
        let explainStr = "";
        let invoiceIds = []; // Array to collect invoice IDs
        
        for (let i = 0; i < clientProjectInvoices.length; i++) {
          const hoursWorked = defaultHoursForProject(
            clientProjectInvoices[i].project_name,
            clientProjectInvoices[i].hours_worked,
          );
          totalClientPrice += clientProjectInvoices[i].client_price * hoursWorked;
          totalRecruiterPrice += clientProjectInvoices[i].recruiter_price * hoursWorked;
          //explainStr += clientProjectInvoices[i].candidate + " ($" + clientProjectInvoices[i].client_price + "/hr x " + hoursWorked + " =  $" + clientProjectInvoices[i].client_price * hoursWorked + " <br>";
          if (hoursWorked > 0) {
            explainStr += `<tr><td>${clientProjectInvoices[i].candidate}</td><td>${clientProjectInvoices[i].project_name || 'Technology Services'}</td><td>${hoursWorked}</td><td>${formatNumber(clientProjectInvoices[i].client_price)}</td><td>${formatNumber(clientProjectInvoices[i].client_price * hoursWorked)}</td></tr>`;
            
            // Add the transaction ID to the invoiceIds array for comma-separated string
            if (clientProjectInvoices[i].invoice_id) {
              invoiceIds.push(clientProjectInvoices[i].invoice_id);
            }
          }
          //console.log('debug: totalprice ', totalClientPrice, explainStr);
        }
        //console.log('final explain : ',explainStr);

        // Create comma-separated invoice IDs string for invoice_link field
        const invoiceLink = invoiceIds.join(',');

        const invoicesData = {
          inv_date: invoiceDate,
          due_date: due_date,
          period_start: startPeriod,
          period_end: endPeriod,
          client_id: clientProjectInvoices[0].client_id,
          client_name: clientProjectInvoices[0].client,
          project_name: clientProjectInvoices[0].project_name,
          client_contact: clientProjectInvoices[0].client_contact,
          client_email: clientProjectInvoices[0].client_email,
          client_addr: clientProjectInvoices[0].client_addr,
          client_phone: clientProjectInvoices[0].client_phone,
          explain_str: explainStr,
          inv_html: '',
          inv_hash: '',
          inv_value: totalClientPrice,
          pay_recruiter: totalRecruiterPrice,
          inv_status: "READY",
          invoice_link: invoiceLink // Add the comma-separated invoice IDs
        }
        //console.log("invoice data ", invoicesData);
        // Call the REST API function to submit the invoice for this client-project combination
        const token = sessionStorage.getItem('token'); // Retrieve the token from sessionStorage
        const response = await axios.post(`${process.env.REACT_APP_RYZ_SERVER}/submit_client_invoice?update_inv=1`, invoicesData, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, // Include the token in the request header
          },
        });

        if (response.status === 200) {
          setInvoiceStatus('SUBMITTED');
          setEditedHoursTxnIds(new Set());
          console.log(`Invoice(s) submitted successfully for client: ${clientProjectInvoices[0].client}, project: ${clientProjectInvoices[0].project_name || 'No Project'}`);
          //console.log(response.data);
          // Optionally, you can add further actions here, such as showing a success message to the user
        } else {
          console.error(`Failed to submit invoice(s) for client: ${clientProjectInvoices[0].client}, project: ${clientProjectInvoices[0].project_name || 'No Project'}`);
          // Optionally, handle the error case
        }
      }
    } catch (error) {
      console.error('Error submitting invoice(s):', error);
    } finally {
      setIsSubmitting(false); // Hide spinner
    }
  };


  // Function to save invoice changes
  // Implement the saveInvoice function
  const saveInvoice = async () => {
    setIsSaving(true); // Show spinner
    try {
      let recruiterTotal = 0;
      let clientTotal = 0;
  
      for (const invoice of invoiceTableData) {
        if (isInternalOnlyProject(invoice.project_name)) {
          continue;
        }
        const hoursWorked = isMonthlyProject(invoice.project_name)
          ? 1
          : invoice.hours_worked;
        const recruiterPriceTotal = invoice.recruiter_price * hoursWorked;
        const clientPriceTotal = invoice.client_price * hoursWorked;
        recruiterTotal += recruiterPriceTotal;
        clientTotal += clientPriceTotal;
  
        if (clientTotal < recruiterTotal) {
          throw new Error('Client total is less than recruiter total');
        }
  
        const newInvoice = {
          inv_date: invoiceDate,  // Use the invoiceDate from UI state
          candidate_id: invoice.candidate_id,
          period_start: startPeriod,
          period_end: endPeriod,
          txn_id: invoice.txn_id,
          hours_worked: isMonthlyProject(invoice.project_name)
            ? 1
            : invoice.hours_worked,
          inv_value: invoice.inv_value,  // Provide a default value if inv_value is not available
          inv_status: "SAVED",
          recruiter_price: invoice.recruiter_price,
          recruiter_total: invoice.recruiter_price * hoursWorked,
          client_price: invoice.client_price,
          client_total: invoice.client_price * hoursWorked
        };
        //console.log('newInvoice ', newInvoice);
        const token = sessionStorage.getItem('token'); // Retrieve the token from sessionStorage
        const response = await axios.post(`${process.env.REACT_APP_RYZ_SERVER}/new_invoice?update_inv=1`, newInvoice, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, // Include the token in the request header
          },
        });
        invoice.invoice_id = response.data.id;
      }
  
      console.log('Invoices saved successfully! ', { recruiterTotal, clientTotal });
      setInvoiceStatus('SAVED')
      setSaveClicked(true); // Enable the Create Final Invoice button
      setEditedHoursTxnIds(new Set());
      return [recruiterTotal, clientTotal];
    } catch (error) {
      console.error('Error saving invoices:', error);
    } finally {
      setIsSaving(false); // Hide spinner
    }
  };
  

  // Function to filter invoice table data based on selected client
  const filteredInvoiceTableData = selectedClient === 'All' ? invoiceTableData :
    invoiceTableData.filter(invoice => invoice.client === selectedClient);

  return (
    <div className="invoice-container">
      {/* Loading State */}
      {isLoading && (
        <div className="loading-message">
          <span className="loading-spinner"></span>
          Loading invoice data...
        </div>
      )}

      {/* Error State */}
      {timeoutError && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {timeoutError.message}
          <button
            className="login-redirect-button"
            onClick={() => window.location.href = '/login'}
          >
            Go to Login
          </button>
        </div>
      )}

      {!isLoading && !timeoutError && (
      <>
      <div className="panel panel-1 invoice-details-panel">
        <h2>Enter Invoice Details</h2>
        <div className="invoice-details__controls">
          <div className="invoice-details__control invoice-details__control--month">
            <label htmlFor="billingMonth">Month</label>
            <select
              id="billingMonth"
              value={billingMonth}
              onChange={(e) => setBillingMonth(e.target.value)}
            >
              {BILLING_MONTH_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="invoice-details__control invoice-details__control--year">
            <label htmlFor="billingYear">Year</label>
            <select
              id="billingYear"
              value={billingYear}
              onChange={(e) => setBillingYear(e.target.value)}
            >
              {yearSelectChoices.map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="invoice-details__control invoice-details__control--client">
            <label htmlFor="client">Client</label>
            <select id="client" value={selectedClient} onChange={handleClientChange}>
              <option value="All">All</option>
              {clientOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="invoice-details__summary" aria-label="Computed billing period">
          <div className="invoice-details__summary-item">
            <span className="invoice-details__summary-label">Invoice date</span>
            <span className="invoice-details__summary-value">{formatDisplayDate(invoiceDate)}</span>
          </div>
          <div className="invoice-details__summary-item">
            <span className="invoice-details__summary-label">Period start</span>
            <span className="invoice-details__summary-value">{formatDisplayDate(startPeriod)}</span>
          </div>
          <div className="invoice-details__summary-item">
            <span className="invoice-details__summary-label">Period end</span>
            <span className="invoice-details__summary-value">{formatDisplayDate(endPeriod)}</span>
          </div>
        </div>
      </div>
      <div className="panel panel-2">
        <h2>Enter Candidate Hours</h2>
        <h3>Total entered: {filteredInvoiceTableData.filter(inv => isMonthlyProject(inv.project_name) || editedHoursTxnIds.has(inv.txn_id)).length} out of {filteredInvoiceTableData.length}</h3>
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Project</th>
              <th>Candidate</th>
              <th>Hours</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoiceTableData.map((invoice) => {
              const monthly = isMonthlyProject(invoice.project_name);
              const hoursVal = Number(invoice.hours_worked);
              const isZeroHours = !monthly && (!Number.isFinite(hoursVal) || hoursVal === 0);
              const hoursClass = [
                'invoice-hours-input',
                monthly ? 'invoice-hours-input--locked' : '',
                isZeroHours ? 'invoice-hours-input--zero' : 'invoice-hours-input--filled',
              ].filter(Boolean).join(' ');
              const hoursDisplay = monthly
                ? '1'
                : (isZeroHours ? '' : String(Math.round(hoursVal)));
              return (
              <tr key={invoice.txn_id} className={isZeroHours ? 'invoice-row--zero-hours' : ''}>
                <td>{invoice.client}</td>
                <td>{invoice.project_name && invoice.project_name !== 'NULL' ? invoice.project_name : ''}</td>
                <td>{invoice.candidate}</td>
                <td className={editedHoursTxnIds.has(invoice.txn_id) ? 'hours-edited' : ''}>
                  <input
                    type="text"
                    className={hoursClass}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="off"
                    placeholder=""
                    value={hoursDisplay}
                    readOnly={monthly}
                    disabled={monthly}
                    title={monthly ? 'Monthly rate — quantity is fixed at 1' : undefined}
                    onChange={(e) => handleHoursChange(invoice, e)}
                  />
                </td>
                <td>{invoice.inv_status}</td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
      <div className="panel panel-3">
        <div>
          <button onClick={saveInvoice} disabled={isSaving}>
            {isSaving ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
          <button onClick={submitInvoice} disabled={submitDisabled || isSubmitting} className={submitDisabled ? "submit-button-disabled" : "submit-button-enabled"}>
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Creating Invoice...
              </>
            ) : (
              'Create Final Invoice'
            )}
          </button>
          <h2>  </h2>
          <h3> {invoiceStatus}</h3>
          
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default InvoicePage;
